import React, { useMemo } from 'react';
import { loadOrCreateIdentity } from '../lib/identity';
import { getUserPins, calculateTastemakerScore, calculatePinScore, getVideo } from '../lib/metadata';
import { COLORS, SPACING } from '../styles/theme';

function timeAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 2592000) return `${Math.floor(s / 86400)}d ago`;
  return `${Math.floor(s / 2592000)}mo ago`;
}

export function TastemakerPins({ onBack, onPlayVideo }: { onBack: () => void; onPlayVideo?: (hash: string) => void }) {
  const identity = loadOrCreateIdentity();

  const rows = useMemo(() => {
    const pins = getUserPins(identity.userId);
    return pins
      .map(pin => ({
        pin,
        video: getVideo(pin.videoHash),
        score: calculatePinScore(identity.userId, pin.videoHash),
      }))
      .sort((a, b) => b.score - a.score);
  }, [identity.userId]);

  const total = calculateTastemakerScore(identity.userId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', fontFamily: '"IBM Plex Mono", monospace' }}>
      {/* Header */}
      <div style={{
        padding: SPACING.md,
        borderBottom: `1px solid ${COLORS.border}`,
        display: 'flex', alignItems: 'center', gap: SPACING.md,
        flexShrink: 0,
      }}>
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', color: COLORS.highlight, cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', padding: 0 }}
        >
          ← back
        </button>
        <span style={{ fontSize: '13px', fontWeight: 700, color: COLORS.text }}>Tastemaker Score</span>
        <span style={{
          marginLeft: 'auto',
          backgroundColor: COLORS.accent, color: '#fff',
          padding: '2px 10px', borderRadius: 4,
          fontSize: '14px', fontWeight: 700,
        }}>
          ★ {total}
        </span>
      </div>

      {/* Explainer */}
      <div style={{ padding: `${SPACING.sm} ${SPACING.md}`, borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
        <p style={{ margin: 0, fontSize: '10px', color: COLORS.textMuted, lineHeight: 1.5 }}>
          You earn points by pinning videos early — before they get popular. The earlier you pin, the higher your score on that video.
        </p>
      </div>

      {/* Pin list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {rows.length === 0 ? (
          <div style={{ padding: SPACING.lg, color: COLORS.textMuted, fontSize: '12px' }}>
            No pins yet. Pin videos in the feed to start earning score.
          </div>
        ) : (
          rows.map(({ pin, video, score }) => (
            <div
              key={pin.videoHash}
              onClick={() => video && onPlayVideo?.(pin.videoHash)}
              style={{
                display: 'flex', gap: SPACING.md, padding: SPACING.md,
                borderBottom: `1px solid ${COLORS.border}`,
                cursor: video && onPlayVideo ? 'pointer' : 'default',
                alignItems: 'center',
              }}
            >
              {/* Thumbnail */}
              <div style={{ width: 96, height: 54, flexShrink: 0, backgroundColor: '#111', overflow: 'hidden', borderRadius: 2 }}>
                {video?.thumbnailDataUrl
                  ? <img src={video.thumbnailDataUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.textMuted, fontSize: '16px' }}>▶</div>
                }
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 700, fontSize: '12px', color: COLORS.text,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  marginBottom: 4,
                }}>
                  {video?.title ?? pin.videoHash.slice(0, 16) + '…'}
                </div>
                <div style={{ fontSize: '10px', color: COLORS.textMuted }}>
                  pinned {timeAgo(pin.pinnedAt)}
                  {score === 0 && <span style={{ marginLeft: 8, fontStyle: 'italic' }}>needs more pins to score</span>}
                </div>
              </div>

              {/* Score */}
              <div style={{
                flexShrink: 0, minWidth: 48, textAlign: 'right',
                fontSize: '14px', fontWeight: 700,
                color: score > 0 ? COLORS.accent : COLORS.textMuted,
              }}>
                {score > 0 ? `+${score}` : '—'}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Total footer */}
      {rows.length > 0 && (
        <div style={{
          borderTop: `2px solid ${COLORS.border}`,
          padding: SPACING.md,
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
          gap: SPACING.sm, flexShrink: 0,
          backgroundColor: COLORS.panel,
        }}>
          <span style={{ fontSize: '11px', color: COLORS.textMuted }}>total</span>
          <span style={{ fontSize: '16px', fontWeight: 700, color: COLORS.accent }}>★ {total}</span>
        </div>
      )}
    </div>
  );
}
